import type { CustomLLMProvider } from '../types';

// Stand Oktober 2026 (ai.google.dev/gemini-api/docs/models). 2.0 und 1.5 sind abgeschaltet,
// 2.5 steht nur noch Konten offen, die es bereits genutzt haben.
export const GEMINI_MODELS = [
  {
    id: 'gemini-3.8-flash',
    label: 'Gemini 3.8 Flash',
    description: 'Standard. Stärkstes Flash-Modell, mit Free Tier.',
    isNew: true
  },
  {
    id: 'gemini-4-argon',
    label: 'Gemini 4 Argon',
    description: 'Flaggschiff für schwieriges Denken. Kein Free Tier, nur mit Abrechnung.',
    isNew: true
  },
  {
    id: 'gemini-3.1-pro-preview',
    label: 'Gemini 3.1 Pro (Preview)',
    description: 'Pro-Modell als Vorschau. Kein Free Tier, nur mit Abrechnung.',
    isExperimental: true
  },
  {
    id: 'gemini-3.6-flash',
    label: 'Gemini 3.6 Flash',
    description: 'Vorgänger von 3.8 Flash, mit Free Tier.'
  },
  {
    id: 'gemini-3.5-flash-lite',
    label: 'Gemini 3.5 Flash-Lite',
    description: 'Schnellstes und günstigstes Modell, mit Free Tier.'
  },
  {
    id: 'gemini-3.1-flash-lite',
    label: 'Gemini 3.1 Flash-Lite',
    description: 'Älteres Lite-Modell, mit Free Tier. Abschaltung frühestens 7. Mai 2027.'
  },
  {
    id: 'gemini-2.5-flash',
    label: 'Gemini 2.5 Flash (Legacy)',
    description: 'Nur für Konten, die 2.5 bereits genutzt haben.'
  }
];

export const getModelInfo = (modelId: string) => {
  return GEMINI_MODELS.find(m => m.id === modelId);
};


export const DEFAULT_GEMINI_MODEL = 'gemini-3.8-flash';

// ── LLM Call Abstraction ────────────────────────────────────────────────────

interface LLMCallOptions {
  /** Einzelne Anfrage oder Chatverlauf; Verläufe gehen als echte Gesprächsrunden an das Modell */
  contents: string | ChatMessage[];
  systemInstruction?: string;
  /** If set, use a custom OpenAI-compatible provider instead of backend Gemini */
  customProvider?: CustomLLMProvider;
  /** Model override; falls back to provider default or Gemini default */
  model?: string;
  /** Optional AbortSignal for cancellation */
  signal?: AbortSignal;
}

/**
 * Call the LLM – either via the Prompt Manager backend (Gemini)
 * or directly via a user-configured OpenAI-compatible endpoint.
 */
const callLLM = async (options: LLMCallOptions): Promise<string> => {
  const { contents, systemInstruction, customProvider, model, signal } = options;

  // ── Custom OpenAI-compatible provider ──
  if (customProvider) {
    return callCustomProvider({ contents, systemInstruction, provider: customProvider, model, signal });
  }

  // ── Default: Prompt Manager backend (Gemini) ──
  return callGeminiBackend({ contents, systemInstruction, model, signal });
};

// ── Gemini backend call ─────────────────────────────────────────────────────
const toGeminiContents = (contents: string | ChatMessage[]) =>
  typeof contents === 'string'
    ? contents
    : contents.map((m) => ({ role: m.role === 'assistant' ? 'model' : 'user', parts: [{ text: m.content }] }));

const callGeminiBackend = async (opts: {
  contents: string | ChatMessage[];
  systemInstruction?: string;
  model?: string;
  signal?: AbortSignal;
}): Promise<string> => {
  const controller = new AbortController();
  // Denkende Modelle (3.8 Flash, 4 Argon) brauchen bei langen Prompts deutlich mehr als eine Minute
  const timeout = setTimeout(() => controller.abort(), 180000);
  // Link external signal to internal controller
  if (opts.signal) {
    if (opts.signal.aborted) { clearTimeout(timeout); throw new DOMException('Aborted', 'AbortError'); }
    opts.signal.addEventListener('abort', () => controller.abort(), { once: true });
  }
  try {
    const response = await fetch('/api/gemini', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        contents: toGeminiContents(opts.contents),
        systemInstruction: opts.systemInstruction,
        model: opts.model,
      }),
      signal: controller.signal,
    });

    const data = await response.json();
    if (!response.ok) {
      const errMsg = typeof data.error === 'string' ? data.error : 'API-Anfrage fehlgeschlagen.';
      throw new Error(errMsg);
    }
    return data.text || '';
  } catch (error) {
    console.warn('Gemini API request failed.', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Ein unbekannter Fehler ist bei der Kontaktaufnahme mit der API aufgetreten.');
  } finally {
    clearTimeout(timeout);
  }
};

// ── Custom OpenAI-compatible provider call ──────────────────────────────────
const callCustomProvider = async (opts: {
  contents: string | ChatMessage[];
  systemInstruction?: string;
  provider: CustomLLMProvider;
  model?: string;
  signal?: AbortSignal;
}): Promise<string> => {
  const { contents, systemInstruction, provider, model, signal } = opts;
  const resolvedModel = model || provider.defaultModel;

  const messages: { role: string; content: string }[] = [];
  if (systemInstruction) {
    messages.push({ role: 'system', content: systemInstruction });
  }
  if (typeof contents === 'string') {
    messages.push({ role: 'user', content: contents });
  } else {
    messages.push(...contents.map((m) => ({ role: m.role, content: m.content })));
  }

  const baseUrl = provider.baseUrl.replace(/\/$/, '');
  const endpoint = `${baseUrl}/chat/completions`;

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 90000);
  // Link external signal to internal controller
  if (signal) {
    if (signal.aborted) { clearTimeout(timeout); throw new DOMException('Aborted', 'AbortError'); }
    signal.addEventListener('abort', () => controller.abort(), { once: true });
  }
  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${provider.apiKey}`,
      },
      body: JSON.stringify({ model: resolvedModel, messages }),
      signal: controller.signal,
    });

    const data = await response.json();
    if (!response.ok) {
      const errMsg = data?.error?.message || data?.error || 'Anfrage fehlgeschlagen.';
      throw new Error(`Fehler von "${provider.name}": ${errMsg}`);
    }
    return data?.choices?.[0]?.message?.content || '';
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(`Unbekannter Fehler bei "${provider.name}".`);
  } finally {
    clearTimeout(timeout);
  }
};

// ── LLM context helper (builds call options from settings) ─────────────────
export interface LLMContext {
  selectedProviderId?: string;
  selectedModel?: string;
  testModel?: string;
  generatorModel?: string;
  optimizerModel?: string;
  customProviders?: CustomLLMProvider[];
}

export const buildLLMCallOptions = (context?: LLMContext, feature?: 'test' | 'generator' | 'optimizer'): Pick<LLMCallOptions, 'customProvider' | 'model'> => {
  if (!context) return {};
  const { selectedProviderId, selectedModel, customProviders, testModel, generatorModel, optimizerModel } = context;

  let specificModel = selectedModel;
  if (feature === 'test' && testModel) specificModel = testModel;
  if (feature === 'generator' && generatorModel) specificModel = generatorModel;
  if (feature === 'optimizer' && optimizerModel) specificModel = optimizerModel;

  // If a custom provider is selected
  if (selectedProviderId && selectedProviderId !== 'gemini' && customProviders?.length) {
    const provider = customProviders.find((p) => p.id === selectedProviderId);
    if (provider) {
      return { customProvider: provider, model: specificModel || provider.defaultModel };
    }
  }

  return { model: specificModel };
};

// ── Public API ──────────────────────────────────────────────────────────────

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

const LANGUAGE_RULE = 'Antworte auf Deutsch, außer der Prompt verlangt ausdrücklich eine andere Sprache.';

// Standard für „Prompt testen“, solange in den Einstellungen kein eigener globaler Kontext steht.
// Der Prompt selbst bleibt unverändert, damit der Test zeigt, was er wirklich bewirkt.
export const DEFAULT_TEST_SYSTEM_PROMPT = `Du bekommst einen Prompt, den jemand gerade entwickelt und ausprobieren möchte. Bearbeite ihn genau so, als wäre er dir direkt gestellt worden – deine Antwort soll zeigen, was dieser Prompt tatsächlich bewirkt.
- Führe den Prompt aus. Kommentiere, bewerte oder verbessere ihn nicht.
- Stehen noch Platzhalter wie {{thema}} im Text, setze ein plausibles Beispiel ein und markiere es mit [Beispiel: …].
- Bei Folgefragen bleibst du in derselben Rolle und beziehst dich auf deine bisherigen Antworten.
- ${LANGUAGE_RULE}`;

const buildTestSystemInstruction = (globalSystemInstruction?: string) =>
  globalSystemInstruction?.trim()
    ? `${globalSystemInstruction.trim()}\n\n${LANGUAGE_RULE}`
    : DEFAULT_TEST_SYSTEM_PROMPT;

export const testPrompt = async (
  prompt: string,
  globalSystemInstruction?: string,
  llmContext?: LLMContext,
  signal?: AbortSignal
): Promise<string> => {
  return callLLM({ contents: prompt, systemInstruction: buildTestSystemInstruction(globalSystemInstruction), ...buildLLMCallOptions(llmContext, 'test'), signal });
};

/** Chat-based test: allows iterative follow-up questions on test results */
export const testPromptChat = async (
  messages: ChatMessage[],
  globalSystemInstruction?: string,
  llmContext?: LLMContext,
  signal?: AbortSignal
): Promise<string> => {
  return callLLM({ contents: messages, systemInstruction: buildTestSystemInstruction(globalSystemInstruction), ...buildLLMCallOptions(llmContext, 'test'), signal });
};

export const DEFAULT_OPTIMIZER_PROMPT = `Du bist Prompt-Engineer. Du bekommst einen Prompt und überarbeitest ihn so, dass ein großes Sprachmodell ihn zuverlässig, präzise und im gewünschten Format beantwortet. Du führst den Prompt nicht aus.

So gehst du vor:
1. Erkenne Ziel, Art (Aufgabenprompt, Systemprompt, Skill-Anleitung, Vorlage) und Zielgruppe. Die Art bleibt erhalten.
2. Mache das Ziel eindeutig: Was genau soll herauskommen, und woran erkennt man ein gutes Ergebnis?
3. Ergänze nur, was die Antwort spürbar verbessert: Rolle oder Fachperspektive, nötigen Kontext, klare Arbeitsschritte, Ausgabeformat mit Umfang, Randbedingungen und den Umgang mit Unklarheiten. Streiche Floskeln und Wiederholungen.
4. Gliedere lange Prompts mit kurzen Überschriften oder Aufzählungen. Kurze Prompts bleiben kurz.
5. Erfinde keine Fakten, Zahlen oder Vorgaben. Fehlt eine Angabe, die nur der Nutzer kennt, setze einen Platzhalter {{name}} oder {{name:Vorschlag}}.
6. Übernimm vorhandene Platzhalter ({{…}}), Codeblöcke, Beispiele und Fachbegriffe unverändert.
7. Schreibe in der Sprache des Original-Prompts.

Steht unter dem Prompt eine „Zusätzliche Optimierungsanweisung“, gehört sie nicht zum Prompt, sondern ist ein Wunsch an dich. Spätere Nachrichten sind Änderungswünsche zur letzten Fassung: setze sie um und gib wieder die vollständige Fassung aus.

Ausgabe: ausschließlich der fertige Prompt als reiner Text – ohne Einleitung, ohne Erklärung, ohne Überschrift und ohne umschließenden Codeblock.`;

export const DEFAULT_GENERATOR_PROMPT = `Du bist Prompt-Engineer und entwickelst mit mir in mehreren Runden einen Prompt für ein großes Sprachmodell. Meine erste Nachricht beschreibt die Aufgabe, für die der Prompt gebraucht wird – auch wenn sie knapp ist. Du schreibst den Prompt, du führst ihn nicht aus.

Antworte in jeder Runde genau in diesem Aufbau:

### 1. Optimierter Prompt
Der bisher beste vollständige Prompt in einem einzigen Codeblock (\`\`\`text … \`\`\`). Er muss ohne diesen Chat verständlich sein: Ziel, nötiger Kontext, Rolle oder Fachperspektive, wenn sie hilft, Arbeitsschritte, Ausgabeformat und Randbedingungen. Was ich noch nicht festgelegt habe, steht als Platzhalter {{name}} oder {{name:Vorschlag}} darin. Erfinde keine Fakten.

### 2. Verbesserungsvorschläge
Höchstens vier konkrete Ergänzungen, die die Antwortqualität am meisten heben würden, je eine Zeile mit kurzer Begründung.

### 3. Klärungsfragen
Höchstens drei gezielte Fragen zu den wichtigsten offenen Punkten, die wichtigste zuerst, wo es passt mit Antwortmöglichkeiten.

Arbeite meine Antworten in der nächsten Runde vollständig in den Prompt ein. Schreibe den Prompt in meiner Sprache, sofern ich nichts anderes verlange. Sage ich, dass der Prompt fertig ist, gib nur noch Abschnitt 1 aus.`;

// Wer früher „Standard“ geklickt und gespeichert hat, hat den alten Standardtext in den Einstellungen.
// Der gilt weiter als Standard, damit solche Konten die überarbeitete Fassung bekommen.
const LEGACY_DEFAULTS: [string, string][] = [
  ['You are an expert in prompt engineering. Your task is to analyze the following user-provided prompt', 'The rewritten, improved prompt must also be in German.'],
  ['Rolle: Du agierst als "Prompt-Ingenieur-Assistent".', 'indem du mich nach meiner ursprünglichen Idee für den Prompt fragst.'],
];

export const resolveSystemPrompt = (custom: string | undefined, fallback: string): string => {
  const text = custom?.trim();
  if (!text) return fallback;
  const isLegacyDefault = LEGACY_DEFAULTS.some(([start, end]) => text.startsWith(start) && text.endsWith(end));
  return isLegacyDefault ? fallback : text;
};

// Der globale Kontext der Test-Funktion gilt hier bewusst nicht: er beschreibt, wie ein Prompt
// ausgeführt wird, und würde der Anweisung widersprechen, ihn nur zu überarbeiten.
export const optimizePrompt = async (
  messages: ChatMessage[] | string,
  customOptimizerPrompt?: string,
  llmContext?: LLMContext,
  signal?: AbortSignal
): Promise<string> => {
  const systemInstruction = resolveSystemPrompt(customOptimizerPrompt, DEFAULT_OPTIMIZER_PROMPT);
  return callLLM({ contents: messages, systemInstruction, ...buildLLMCallOptions(llmContext, 'optimizer'), signal });
};

export const suggestTags = async (promptContent: string): Promise<string[]> => {
  try {
    const response = await fetch('/api/gemini/tags', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ promptContent }),
    });

    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data.tags) ? data.tags.map(String) : [];
  } catch (error) {
    console.warn('Error suggesting tags.');
    return [];
  }
};

export const chatPromptEngineer = async (
  messages: ChatMessage[],
  customSystemPrompt?: string,
  llmContext?: LLMContext,
  signal?: AbortSignal
): Promise<string> => {
  return callLLM({
    contents: messages,
    systemInstruction: resolveSystemPrompt(customSystemPrompt, DEFAULT_GENERATOR_PROMPT),
    ...buildLLMCallOptions(llmContext, 'generator'),
    signal,
  });
};
